<!--
/**
 * Port of apps/admin/src/components/Notification.vue (Phase 2.3).
 *
 * Global, self-contained dialog mounted by App.vue. Listens on the
 * 'show-notification' channel (old EventBus → useAppEvents, same name/payload)
 * and shows a persistent modal until the user closes it; closing marks the
 * notification as read (app-core Notification.markAsRead → POST /notifications/seen)
 * unless impersonating.
 *
 * Parity notes:
 * - The old v-dialog was NOT fullscreen on xs → :fullscreen-enabled="false".
 * - Heading/button color: notification.type === 'info' → primary, else the type's
 *   color — mapped to semantic classes.
 * - The old v-alert overrode its color to `primary` for info-type notifications;
 *   the inline type→color mapping (ex-KAlert) renders info notifications info-blue
 *   instead of brand primary (G3-class hue divergence, visual only).
 * - Old overlay color #2c2c38/0.8 → UModal default overlay (visual only).
 */
-->
<template>
    <UModal
        :open="visible"
        :dismissible="false"
        :close="false"
        :ui="{ content: 'sm:max-w-[510px]' }"
    >
        <template #content>
            <div v-if="notification">
                <!-- Old: v-card-title.notification-heading — type-colored bar, white centered text -->
                <div
                    class="flex justify-center p-3 text-screen-title text-inverted"
                    :class="headingBgClass"
                >
                    <!-- Invoice Generated Notification -->
                    <span v-if="notification.code === 'invoice_generated'">
                        {{ t('notification.invoice_generated_heading', { month: getMonth(notification.data.month) }) }}
                    </span>

                    <span v-else-if="notification.code">{{ t(`notification.${notification.code}_heading`) }}</span>
                    <span v-else>{{ t('notification.heading') }}</span>
                </div>

                <div class="p-6">
                    <UAlert :color="alertColor" :icon="alertIcon" variant="soft">
                        <template #description>
                            <!-- Invoice Generated Notification -->
                            <template v-if="notification.code === 'invoice_generated'">
                                <span>
                                    {{ t('notification.invoice_generated', {
                                        software: 'Konty',
                                        amount: Money.parse(notification.data.amount).toFormat(),
                                        month: getMonth(notification.data.month)
                                    }) }}
                                </span>
                                <div class="mt-2">{{ t('notification.invoice_generated_check') }}</div>
                            </template>

                            <template v-else-if="notification.code">
                                {{ t(`notification.${notification.code}`) }}
                            </template>

                            <span v-else>
                                {{ notification.text }}
                            </span>
                        </template>
                    </UAlert>

                    <div class="mt-6 mb-1 flex justify-center">
                        <UButton :color="buttonColor" size="lg" @click="hide()">
                            {{ t('general.close') }}
                        </UButton>
                    </div>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import { Money } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'
import { useAppEvents, type AppEventPayloads } from '@/composables/useAppEvents'

const { t } = useI18n()
const appEvents = useAppEvents()
const { impersonate } = storeToRefs(useAppStore())

/**
 * The runtime payload is an app-core Notification model instance (UserLogin emits
 * `Notification.unread()[0]`); the typed channel only declares the plain fields,
 * so the model members this dialog also consumes (`text`, `markAsRead`) are
 * added locally.
 */
type ShownNotification = AppEventPayloads['show-notification']['notification'] & {
    text?: string | null
    markAsRead: () => Promise<void>
}

const visible = ref(false)
const notification = ref<ShownNotification | null>(null)

/** Old heading class: `notification.type === 'info' ? 'primary' : notification.type`. */
const HEADING_BG: Record<string, string> = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error'
}

const headingBgClass = computed(() => HEADING_BG[notification.value?.type ?? ''] ?? 'bg-primary')

type ButtonColor = 'primary' | 'success' | 'warning' | 'error'

/** Old button color: `notification.type === 'info' ? 'primary' : notification.type`. */
const BUTTON_COLOR: Record<string, ButtonColor> = {
    success: 'success',
    warning: 'warning',
    error: 'error'
}

const buttonColor = computed<ButtonColor>(() => BUTTON_COLOR[notification.value?.type ?? ''] ?? 'primary')

type AlertType = 'success' | 'info' | 'warning' | 'error'
const ALERT_TYPES: readonly string[] = ['success', 'info', 'warning', 'error']

/** Old `:type="notification.type"` on the v-alert — narrowed to the Vuetify alert types. */
const alertType = computed<AlertType | undefined>(() => {
    const type = notification.value?.type
    return type && ALERT_TYPES.includes(type) ? type as AlertType : undefined
})

/** Vuetify v-alert type → color + auto icon (ex-KAlert mapping). */
const ALERT_ICON: Record<AlertType, string> = {
    success: 'i-lucide-circle-check',
    info: 'i-lucide-info',
    warning: 'i-lucide-triangle-alert',
    error: 'i-lucide-circle-x'
}
const alertColor = computed(() => alertType.value ?? 'neutral')
const alertIcon = computed(() => alertType.value ? ALERT_ICON[alertType.value] : undefined)

// created() — register the listener; beforeDestroy() — remove it.
const offShowNotification = appEvents.on('show-notification', showNotification)
onBeforeUnmount(offShowNotification)

function showNotification(data: AppEventPayloads['show-notification']) {
    notification.value = data.notification as ShownNotification
    visible.value = true
}

function hide() {
    markAsSeen()
    notification.value = null
    visible.value = false
}

function markAsSeen() {
    // Old code read `this.impersonate`, which the global mixin never mapped (the
    // guard was inert); ported to the store flag it was meant to read, so
    // impersonated sessions no longer mark tenant notifications as read.
    if (impersonate.value) return
    // Old code called markAsRead() unconditionally — hide() is only reachable from
    // the close button, which renders only while a notification is set.
    notification.value!.markAsRead()
}

function getMonth(month: string): string {
    return t(`months.${moment(month, 'MM/YYYY').format('MMMM').toLowerCase()}`) +
        ` ${moment(month, 'MM/YYYY').format('YYYY')}.`
}
</script>
